import React from "react";
import "./NavigationalListComponent.css";
import Sidebar from "../../Navigation/Sidebar/Sidebar";
import leftArrow from "../../../assests/leftArrow.svg";
import { useNavigate } from "react-router-dom";
import LoadingSpinner from "../../Loading/component";
import { Eye, Plus, Search } from "lucide-react";

const DEFAULT_ICON = "https://cdn-icons-png.flaticon.com/512/1261/1261163.png";
const DEFAULT_BACKGROUND = "https://images.unsplash.com/photo-1505740420928-5e560c06d30e?w=400&h=400&fit=crop";

const NavigationalListComponent = ({
  navigationalList = [],
  loading,
  searchValue,
  onSearchChange,
  activeTab,
  onTabChange,
  statusFilter,
  onStatusFilterChange,
  onCreateNavigational,
  onCardClick,
  canPerformActions,
}) => {
  const navigate = useNavigate();
  const handleBackClick = () => navigate(-1);

  if (loading) {
    return (
      <div className="navigational-list-main-container">
        <Sidebar />
        <div className="navigational-list-loading-overlay">
          <LoadingSpinner />
          <p className="navigational-list-loading-text">Loading navigational list...</p>
        </div>
      </div>
    );
  }

  // Get all variants with details from variantDetails
  const getAllVariantsWithDetails = () => {
    const variants = [];
    navigationalList.forEach(category => {
      // Check if variantDetails exists and has data
      if (category.variantDetails && category.variantDetails.length > 0) {
        category.variantDetails.forEach(variantDetail => {
          // Use 'id' from variantDetail as variantId
          const variantId = variantDetail.id;
          // Use 'name' from variantDetail as variantName
          const variantName = variantDetail.name || `Variant ${variantId}`;
          
          // Apply search filter - search by variant ID or variant name
          if (!searchValue || 
              String(variantId).toLowerCase().includes(searchValue.toLowerCase()) ||
              (variantName && variantName.toLowerCase().includes(searchValue.toLowerCase()))) {
            variants.push({
              categoryId: category.id,
              categoryName: category.name,
              variantId: variantId,
              variantName: variantName,
              variantImage: variantDetail.image,
              variantDetails: variantDetail,
              status: category.status,
              createdAt: category.createdAt,
            });
          }
        });
      } 
      // Fallback to variantIds if variantDetails doesn't exist
      else if (category.variantIds && category.variantIds.length > 0) {
        category.variantIds.forEach(variantId => {
          if (!searchValue || String(variantId).toLowerCase().includes(searchValue.toLowerCase())) {
            variants.push({
              categoryId: category.id,
              categoryName: category.name,
              variantId: variantId,
              variantName: `Variant ${variantId}`,
              variantImage: null,
              variantDetails: null,
              status: category.status,
              createdAt: category.createdAt,
            });
          }
        });
      }
    });
    return variants;
  };

  const allVariants = getAllVariantsWithDetails();

  // Group variants by category for variant view
  const getGroupedVariantsByCategory = () => {
    const grouped = {};
    
    // First filter variants based on status filter
    let filteredVariants = allVariants;
    if (statusFilter !== "All") {
      filteredVariants = allVariants.filter(variant => variant.status === statusFilter);
    }
    
    filteredVariants.forEach(variant => {
      if (!grouped[variant.categoryId]) {
        grouped[variant.categoryId] = {
          categoryId: variant.categoryId,
          categoryName: variant.categoryName,
          categoryStatus: variant.status,
          createdAt: variant.createdAt,
          variants: []
        };
      }
      grouped[variant.categoryId].variants.push(variant);
    });
    
    return Object.values(grouped);
  };

  const groupedCategories = getGroupedVariantsByCategory();

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'Recently';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch (e) {
      return 'Recently';
    }
  };

  // Handle eye icon click - redirect to update page
  const handleEyeIconClick = (categoryId, event) => {
    event.stopPropagation();
    onCardClick(categoryId, "variant");
  };

  return (
    <div className="navigational-list-main-container">
      <Sidebar />

      <div className="navigational-list-content-area">
        {/* Header */}
        <div className="navigational-list-header-wrapper">
          <button onClick={handleBackClick} className="navigational-list-back-button">
            <img src={leftArrow} alt="Back" />
          </button>
          <h1 className="navigational-list-title">Navigational List</h1>
        </div>

        {/* Tabs */}
        <div className="navigational-list-tabs-wrapper">
          <button
            className={`navigational-list-tab-button ${activeTab === "category" ? "navigational-list-tab-button-active" : ""}`}
            onClick={() => onTabChange("category")}
          >
            Category View
            {activeTab === "category" && <div className="navigational-list-tab-indicator" />}
          </button>
          <button
            className={`navigational-list-tab-button ${activeTab === "variant" ? "navigational-list-tab-button-active" : ""}`}
            onClick={() => onTabChange("variant")}
          >
            Variant View
            {activeTab === "variant" && <div className="navigational-list-tab-indicator" />}
          </button>
        </div>

        {/* Search and Filter Bar */}
        <div className="navigational-list-search-filter-section">
          <div className="navigational-list-search-wrapper">
            <Search className="navigational-list-search-icon" size={18} />
            <input
              type="text"
              placeholder={activeTab === "category" ? "Search by Navigational..." : "Search by Variant ID or Name..."}
              value={searchValue}
              onChange={(e) => onSearchChange(e.target.value)}
              className="navigational-list-search-input"
            />
          </div>

          <div className="navigational-list-status-filter-wrapper">
            {["All", "Active", "Inactive"].map((status) => (
              <button
                key={status}
                className={`navigational-list-status-btn ${statusFilter === status ? "navigational-list-status-btn-active" : ""}`}
                onClick={() => onStatusFilterChange(status)}
              >
                {status}
              </button>
            ))}
          </div>
        </div>

        {/* Category View */}
        {activeTab === "category" && (
          <div className="navigational-list-scrollable-content">
            <div className="navigational-list-card-grid">
              {/* Add New Card */}
              {canPerformActions && (
                <div className="navigational-list-add-card" onClick={onCreateNavigational}>
                  <div className="navigational-list-add-icon-outer">
                    <div className="navigational-list-add-icon-inner">
                      <Plus size={32} strokeWidth={2.5} />
                    </div>
                  </div>
                  <span className="navigational-list-add-label">Add a Navigational</span>
                </div>
              )}

              {/* Existing Cards - Filter by status for category view */}
              {navigationalList
                .filter(item => statusFilter === "All" || item.status === statusFilter)
                .filter(item => {
                  if (!searchValue) return true;
                  const name = (item.name || "").toLowerCase();
                  return name.includes(searchValue.toLowerCase());
                })
                .map((item) => {
                  const isInactive = item.status === "Inactive";
                  return (
                    <div 
                      key={item.id} 
                      className={`navigational-list-item-card ${isInactive ? 'navigational-list-item-card-inactive' : ''}`}
                      onClick={() => onCardClick(item.id)}
                    >
                      <div className="navigational-list-card-background">
                        <img 
                          src={item.backgroundUrl || DEFAULT_BACKGROUND} 
                          alt="" 
                          className="navigational-list-card-bg-img"
                          onError={(e) => { e.target.src = DEFAULT_BACKGROUND; }}
                        />
                        <div className="navigational-list-card-overlay" />
                      </div>

                      <div className="navigational-list-card-content">
                        <div className="navigational-list-card-icon-section">
                          <div className="navigational-list-card-icon-wrapper">
                            <img 
                              src={item.iconUrl || DEFAULT_ICON} 
                              alt={item.name} 
                              className="navigational-list-card-icon"
                              onError={(e) => { e.target.src = DEFAULT_ICON; }}
                            />
                          </div>
                        </div>

                        <div className="navigational-list-card-body">
                          <h3 className="navigational-list-card-title">{item.name}</h3>
                          {item.description && (
                            <p className="navigational-list-card-description">
                              {item.description.substring(0, 60)}
                              {item.description.length > 60 ? "..." : ""}
                            </p>
                          )}
                          {/* <div className="navigational-list-card-status-badge">
                            <span className={`status-badge ${item.status === "Active" ? "active" : "inactive"}`}>
                              {item.status}
                            </span>
                          </div> */}
                        </div>
                      </div>
                    </div>
                  );
                })}

              {navigationalList.filter(item => statusFilter === "All" || item.status === statusFilter)
                .filter(item => {
                  if (!searchValue) return true;
                  const name = (item.name || "").toLowerCase();
                  return name.includes(searchValue.toLowerCase());
                }).length === 0 && (
                <div className="navigational-list-empty-state">
                  <div className="navigational-list-empty-icon">📦</div>
                  <h3>No Navigational Items Found</h3>
                  <p>
                    {searchValue
                      ? `No results found for "${searchValue}"`
                      : 'Click "Add a Navigational" to create your first item.'}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Variant View - Shows Variant Details */}
        {activeTab === "variant" && (
          <div className="navigational-list-scrollable-content">
            <div className="navigational-list-variant-view">
              {groupedCategories.length > 0 ? (
                groupedCategories.map((category) => {
                  const isInactive = category.categoryStatus === "Inactive";
                  
                  return (
                    <div key={category.categoryId} className="navigational-list-variant-section">
                      <div className="navigational-list-variant-section-header">
                        <div className="navigational-list-header-slug"></div>
                        <h3 className="navigational-list-category-name">{category.categoryName}</h3>
                        <div className="navigational-list-variant-header-actions">
                         
                          <button 
                            className="navigational-list-view-category-btn"
                            onClick={(e) => handleEyeIconClick(category.categoryId, e)}
                            title="Edit Category"
                          >
                            <Eye size={16} />
                          </button>
                        </div>
                      </div>

                      <div className="navigational-list-variant-table-wrapper">
                        <table className="navigational-list-variant-table">
                          <thead>
                            <tr>
                              <th>Variant ID</th>
                              <th>Variant Name</th>
                              <th>Added Date</th>
                            </tr>
                          </thead>
                          <tbody>
                            {category.variants.map((variant, idx) => (
                              <tr key={`${category.categoryId}-${variant.variantId}-${idx}`}>
                                <td className="navigational-list-variant-id">{variant.variantId}</td>
                                <td className="navigational-list-variant-name">{variant.variantName}</td>
                                <td className="navigational-list-variant-date">
                                  {formatDate(category.createdAt)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  );
                })
              ) : (
                <div className="navigational-list-empty-state">
                  <div className="navigational-list-empty-icon">🔍</div>
                  <h3>No Variants Found</h3>
                  <p>
                    {searchValue 
                      ? `No results found for "${searchValue}"` 
                      : "No variants available"}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NavigationalListComponent;